import { useQuery } from "@tanstack/react-query";
import { useEndpoints } from "../api/ApiContext";
import type { DegradedSubsystem, FinancialQuarantineStatus, PartyType } from "../api/types";
import { Banner } from "../components/ui";
import { formatLocal } from "../lib/datetime";

// UX-DESIGN.md §6 shared states: degraded-service banners from GET
// /v1/status (G-48, cmd/core-platform/system_status.go:89-148). Each
// `degraded` entry is a stable {subsystem, reason} code, and `since` is
// only present when the server knows it (omitempty, never null).
//
// Financial quarantine (migration 0072, UX-DESIGN.md §6): its own
// dedicated banner, not the generic degraded one, when the caller's
// account is under_review -- a deliberate freeze protecting ledger
// integrity, listing exactly what is paused and saying data is not lost.
// "clear" and "not_checked" show nothing; neither is ever announced as
// "all clear". Payout eligibility (migration 0074) is a real
// request-time rule now, not a stub, so there is nothing to disclaim
// for it here any more.

const BLOCK_COPY: Record<string, string> = {
  new_reservations: "your campaigns won't take new placements",
  payouts: "payouts are paused",
};

function QuarantineBanner({ q }: { q: FinancialQuarantineStatus }) {
  const blocked = q.blocks.map((b) => BLOCK_COPY[b] ?? b.replace(/_/g, " "));
  return (
    <Banner tone="warning" live="polite" title="This account's financial state is temporarily under review">
      <span>
        {q.scope === "platform"
          ? "We're checking our own ledger records across the platform. While we do, "
          : "We found a difference between two of our own records for this account and are checking it. While we do, "}
        {blocked.length ? blocked.join(" and ") : "some actions are paused"}. Existing data, balances and earnings are not lost,
        and nothing else changes. This is a deliberate safety freeze, not an outage; contact support if you need details.
        {q.since ? <span className="xs muted"> Since {formatLocal(q.since)}.</span> : null}
      </span>
    </Banner>
  );
}

interface Copy {
  title: string;
  detail: string;
  /** Which portal the subsystem matters to. */
  audience: PartyType[];
}

function copyFor(d: DegradedSubsystem): Copy {
  switch (d.subsystem) {
    case "cpe_serving":
      return {
        title: d.reason === "circuit_breaker_half_open" ? "Per-engagement ads are recovering" : "Per-engagement ads are paused network-wide",
        detail:
          d.reason === "circuit_breaker_half_open"
            ? "A safety breaker tripped on CPE serving and is letting a little traffic through while it checks. CPE ads may serve less than usual. CPM is unaffected."
            : "A safety breaker has stopped CPE (per-engagement) serving across the network. Nothing is charged for CPE while it's open. CPM is unaffected.",
        audience: ["advertiser", "publisher"],
      };
    case "deposits":
      return {
        title: "Adding funds isn't available on this deployment",
        detail: "No payment provider is configured, so deposits can't be started. Your balance and campaigns are unaffected.",
        audience: ["advertiser"],
      };
    case "payout_onboarding":
      return {
        title: "Payout setup isn't available on this deployment",
        detail: "No payment provider is configured, so payout accounts can't be set up. Earnings keep being recorded.",
        audience: ["publisher"],
      };
    case "account_recovery_delivery":
      return {
        title: "Account-recovery messages can't be delivered right now",
        detail:
          "Password-reset and two-step-verification reset messages aren't being sent on this deployment. Keep your recovery codes somewhere safe — they're the way back in if you lose your device.",
        audience: ["advertiser", "publisher"],
      };
    default:
      return {
        title: "Part of VADS is degraded",
        detail: `The ${d.subsystem.replace(/_/g, " ")} service reports a problem (${d.reason}).`,
        audience: ["advertiser", "publisher"],
      };
  }
}

export function SystemStatusBanners({ partyType }: { partyType: PartyType }) {
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["system-status"],
    queryFn: () => ep.system.status(),
    refetchInterval: 60_000,
    retry: false,
  });
  // A failed status read shows nothing: it must never read as "all clear",
  // and it isn't worth an error banner on every screen.
  if (!q.data) return null;
  const degraded = q.data.degraded.filter((d) => copyFor(d).audience.includes(partyType));
  const quarantine = q.data.financial_quarantine?.state === "under_review" ? q.data.financial_quarantine : null;
  if (degraded.length === 0 && !quarantine) return null;
  return (
    <div className="stack-sm" aria-label="Service status">
      {quarantine ? <QuarantineBanner q={quarantine} /> : null}
      {degraded.map((d) => {
        const c = copyFor(d);
        return (
          <Banner key={`${d.subsystem}:${d.reason}`} tone="warning" title={c.title}>
            <span>
              {c.detail}
              {d.since ? <span className="xs muted"> Since {formatLocal(d.since)}.</span> : null}
            </span>
          </Banner>
        );
      })}
    </div>
  );
}
